/**
 * TextReplaceBuilder — Friendly UI for the Text Replace transform.
 *
 * Modes:
 *   1. Find & Replace — literal text substitution (auto-escapes regex)
 *   2. Extract — first/last N chars, substring from position, before/after delimiter
 *   3. Custom Regex — raw pattern + replacement (power users)
 *
 * All modes include a live test box.
 * Stored model is unchanged: { regexPattern, regexReplacement } on the column.
 * Backend applies via Python re.sub(); JS test converts \1 → $1 for preview.
 */

import React, { useState, useEffect } from 'react';

interface TextReplaceBuilderProps {
  regexPattern: string;
  regexReplacement: string;
  onChange: (pattern: string | undefined, replacement: string | undefined) => void;
}

type BuilderMode = 'find_replace' | 'extract' | 'custom';
type ExtractType = 'first_n' | 'last_n' | 'substring' | 'before' | 'after' | 'year' | 'month' | 'day';

/** Escape special regex characters for literal matching */
const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Convert Python-style backreferences (\1) to JS-style ($1) for live testing */
const toJsReplacement = (repl: string) => repl.replace(/\\(\d+)/g, '$$$1');

/** Try to detect extract type + params from an existing regex pattern */
const detectExtractSettings = (
  pattern: string,
  replacement: string
): { type: ExtractType; param: string; start: string } | null => {
  if (replacement !== '\\1') return null;

  let m: RegExpMatchArray | null;

  // Substring: ^.{SKIP}(.{1,LEN}).*$
  m = pattern.match(/^\^\.\{(\d+)\}\(\.\{1,(\d+)\}\)\.\*\$$/);
  if (m) {
    return { type: 'substring', param: m[2], start: String(parseInt(m[1]) + 1) };
  }

  // First N: ^(.{1,N}).*$
  m = pattern.match(/^\^\(\.\{1,(\d+)\}\)\.\*\$$/);
  if (m) {
    return { type: 'first_n', param: m[1], start: '1' };
  }

  // Last N: ^.*(.{N})$
  m = pattern.match(/^\^\.\*\(\.\{(\d+)\}\)\$$/);
  if (m) {
    return { type: 'last_n', param: m[1], start: '1' };
  }

  // Date parts: year, month, day
  if (pattern === '^(\\d{4})-\\d{2}-\\d{2}.*$') {
    return { type: 'year', param: '', start: '1' };
  }
  if (pattern === '^\\d{4}-(\\d{2})-\\d{2}.*$') {
    return { type: 'month', param: '', start: '1' };
  }
  if (pattern === '^\\d{4}-\\d{2}-(\\d{2}).*$') {
    return { type: 'day', param: '', start: '1' };
  }

  // Before delimiter: ^(.*?)ESCAPED_DELIM.*$
  m = pattern.match(/^\^\(\.\*\?\)(.*)\.\*\$$/);
  if (m && m[1]) {
    const delim = m[1].replace(/\\([.*+?^${}()|[\]\\])/g, '$1');
    return { type: 'before', param: delim, start: '1' };
  }

  // After delimiter: ^.*?ESCAPED_DELIM(.*)$
  m = pattern.match(/^\^\.\*\?(.*)\(\.\*\)\$$/);
  if (m && m[1]) {
    const delim = m[1].replace(/\\([.*+?^${}()|[\]\\])/g, '$1');
    return { type: 'after', param: delim, start: '1' };
  }

  return null;
};

/** Try to detect if the regex is a simple find & replace (escaped literal text) */
const detectFindReplace = (
  pattern: string,
  replacement: string
): { findText: string; replaceText: string } | null => {
  if (!pattern) return null;
  // Backreferences indicate it's not a simple find & replace
  if (/\\[1-9]/.test(replacement)) return null;
  // Try unescaping and re-escaping to see if it round-trips (pure literal)
  const unescaped = pattern.replace(/\\([.*+?^${}()|[\]\\])/g, '$1');
  if (escapeRegex(unescaped) === pattern) {
    return { findText: unescaped, replaceText: replacement };
  }
  return null;
};

const TextReplaceBuilder: React.FC<TextReplaceBuilderProps> = ({
  regexPattern,
  regexReplacement,
  onChange,
}) => {
  // Auto-detect initial mode from existing regex values
  const [mode, setMode] = useState<BuilderMode>(() => {
    if (!regexPattern) return 'find_replace';
    if (detectExtractSettings(regexPattern, regexReplacement)) return 'extract';
    if (detectFindReplace(regexPattern, regexReplacement)) return 'find_replace';
    return 'custom';
  });

  // Find & Replace state — pre-populated if detected
  const [findText, setFindText] = useState(() => {
    const fr = regexPattern ? detectFindReplace(regexPattern, regexReplacement) : null;
    return fr ? fr.findText : '';
  });
  const [replaceText, setReplaceText] = useState(() => {
    const fr = regexPattern ? detectFindReplace(regexPattern, regexReplacement) : null;
    return fr ? fr.replaceText : '';
  });

  // Extract state — pre-populated if detected
  const [extractType, setExtractType] = useState<ExtractType>(() => {
    const ext = regexPattern ? detectExtractSettings(regexPattern, regexReplacement) : null;
    return ext ? ext.type : 'first_n';
  });
  const [extractParam, setExtractParam] = useState(() => {
    const ext = regexPattern ? detectExtractSettings(regexPattern, regexReplacement) : null;
    return ext ? ext.param : '3';
  });
  const [substringStart, setSubstringStart] = useState(() => {
    const ext = regexPattern ? detectExtractSettings(regexPattern, regexReplacement) : null;
    return ext ? ext.start : '1';
  });

  // Test
  const [testInput, setTestInput] = useState('');

  // Generate regex when simple mode inputs change
  useEffect(() => {
    if (mode === 'find_replace') {
      if (!findText) {
        // Keep empty strings (not undefined) so the transform type stays detected
        onChange('', '');
        return;
      }
      onChange(escapeRegex(findText), replaceText);
    } else if (mode === 'extract') {
      const param = extractParam || '';
      switch (extractType) {
        case 'first_n': {
          const n = parseInt(param) || 1;
          onChange(`^(.{1,${n}}).*$`, '\\1');
          break;
        }
        case 'last_n': {
          const n = parseInt(param) || 1;
          onChange(`^.*(.{${n}})$`, '\\1');
          break;
        }
        case 'substring': {
          const len = parseInt(param) || 1;
          const start = parseInt(substringStart) || 1;
          const skip = start - 1;
          onChange(`^.{${skip}}(.{1,${len}}).*$`, '\\1');
          break;
        }
        case 'before': {
          if (!param) { onChange('', ''); break; }
          onChange(`^(.*?)${escapeRegex(param)}.*$`, '\\1');
          break;
        }
        case 'after': {
          if (!param) { onChange('', ''); break; }
          onChange(`^.*?${escapeRegex(param)}(.*)$`, '\\1');
          break;
        }
        case 'year':
          onChange('^(\\d{4})-\\d{2}-\\d{2}.*$', '\\1');
          break;
        case 'month':
          onChange('^\\d{4}-(\\d{2})-\\d{2}.*$', '\\1');
          break;
        case 'day':
          onChange('^\\d{4}-\\d{2}-(\\d{2}).*$', '\\1');
          break;
      }
    }
    // custom mode: user edits regex directly, no generation needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, findText, replaceText, extractType, extractParam, substringStart]);

  // Compute test result (live)
  let testResult = testInput || '';
  if (testInput && regexPattern) {
    try {
      const re = new RegExp(regexPattern, 'g');
      testResult = testInput.replace(re, toJsReplacement(regexReplacement || ''));
    } catch {
      testResult = '(invalid pattern)';
    }
  }

  const handleModeChange = (newMode: BuilderMode) => {
    setMode(newMode);
    if (newMode === 'find_replace') {
      const detected = detectFindReplace(regexPattern, regexReplacement);
      if (detected) {
        setFindText(detected.findText);
        setReplaceText(detected.replaceText);
      } else {
        setFindText('');
        setReplaceText('');
        onChange('', '');
      }
    } else if (newMode === 'extract') {
      const detected = detectExtractSettings(regexPattern, regexReplacement);
      if (detected) {
        setExtractType(detected.type);
        setExtractParam(detected.param);
        setSubstringStart(detected.start);
      } else {
        setExtractType('first_n');
        setExtractParam('3');
        setSubstringStart('1');
      }
      // useEffect will generate regex
    }
    // custom: keep existing regexPattern/regexReplacement as-is
  };

  const isNumericExtract = extractType === 'first_n' || extractType === 'last_n' || extractType === 'substring';
  const isDatePartExtract = extractType === 'year' || extractType === 'month' || extractType === 'day';

  return (
    <div className="transform-config-editor">
      {/* Mode selector */}
      <div className="config-row">
        <label>Mode:</label>
        <div className="format-selector">
          {([
            { key: 'find_replace', label: 'Find & Replace' },
            { key: 'extract', label: 'Extract' },
            { key: 'custom', label: 'Custom Regex' },
          ] as const).map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`format-option ${mode === key ? 'active' : ''}`}
              onClick={() => handleModeChange(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Find & Replace */}
      {mode === 'find_replace' && (
        <>
          <div className="config-row">
            <label>Find:</label>
            <input
              type="text"
              className="form-input config-input"
              value={findText}
              onChange={(e) => setFindText(e.target.value)}
              placeholder="Text to find"
              style={{ maxWidth: 'none', flex: 1 }}
            />
          </div>
          <div className="config-row">
            <label>Replace with:</label>
            <input
              type="text"
              className="form-input config-input"
              value={replaceText}
              onChange={(e) => setReplaceText(e.target.value)}
              placeholder="Leave empty to remove"
              style={{ maxWidth: 'none', flex: 1 }}
            />
          </div>
        </>
      )}

      {/* Extract */}
      {mode === 'extract' && (
        <>
          <div className="config-row">
            <label>Extract:</label>
            <select
              className="form-select form-select-sm"
              value={extractType}
              onChange={(e) => {
                const newType = e.target.value as ExtractType;
                setExtractType(newType);
                const needsNumParam = newType === 'first_n' || newType === 'last_n' || newType === 'substring';
                const isDatePart = newType === 'year' || newType === 'month' || newType === 'day';
                setExtractParam(needsNumParam ? '3' : isDatePart ? '' : '');
                if (newType === 'substring') setSubstringStart('1');
              }}
            >
              <option value="first_n">First N characters</option>
              <option value="last_n">Last N characters</option>
              <option value="substring">Substring (from position)</option>
              <option value="before">Text before delimiter</option>
              <option value="after">Text after delimiter</option>
              <option value="year">Year (YYYY)</option>
              <option value="month">Month (MM)</option>
              <option value="day">Day (DD)</option>
            </select>
          </div>
          {extractType === 'substring' && (
            <div className="config-row">
              <label>Start position:</label>
              <input
                type="number"
                className="form-input config-input"
                value={substringStart}
                onChange={(e) => setSubstringStart(e.target.value)}
                placeholder="1"
                min={1}
                style={{ width: '100px', maxWidth: '100px' }}
              />
            </div>
          )}
          {!isDatePartExtract && (
            <div className="config-row">
              <label>{extractType === 'substring' ? 'Length:' : isNumericExtract ? 'Characters:' : 'Delimiter:'}</label>
              <input
                type={isNumericExtract ? 'number' : 'text'}
                className="form-input config-input"
                value={extractParam}
                onChange={(e) => setExtractParam(e.target.value)}
                placeholder={isNumericExtract ? '3' : 'e.g. - or /'}
                min={isNumericExtract ? 1 : undefined}
                style={{ width: '100px', maxWidth: '100px' }}
              />
            </div>
          )}
          {isDatePartExtract && (
            <div className="config-row">
              <label style={{ fontStyle: 'italic', opacity: 0.7 }}>Extracts from YYYY-MM-DD date format</label>
            </div>
          )}
        </>
      )}

      {/* Custom Regex */}
      {mode === 'custom' && (
        <>
          <div className="config-row">
            <label>Pattern:</label>
            <input
              type="text"
              className="form-input config-input"
              value={regexPattern || ''}
              onChange={(e) => onChange(e.target.value, regexReplacement ?? '')}
              placeholder="e.g. ^(\d{4})-(\d{2})-(\d{2})$"
              style={{ maxWidth: 'none', flex: 1 }}
            />
          </div>
          <div className="config-row">
            <label>Replacement:</label>
            <input
              type="text"
              className="form-input config-input"
              value={regexReplacement || ''}
              onChange={(e) => onChange(regexPattern ?? '', e.target.value)}
              placeholder="e.g. \3/\2/\1"
              style={{ maxWidth: 'none', flex: 1 }}
            />
          </div>
        </>
      )}

      {/* Live test */}
      <div className="text-replace-test">
        <label>Test:</label>
        <input
          type="text"
          className="form-input config-input"
          value={testInput}
          onChange={(e) => setTestInput(e.target.value)}
          placeholder="Type sample text"
          style={{ maxWidth: 'none', flex: 1 }}
        />
        <span className="text-replace-arrow">→</span>
        <span className={`text-replace-result ${testResult === '(invalid pattern)' ? 'text-replace-error' : ''}`}>
          {testResult || '—'}
        </span>
      </div>
    </div>
  );
};

export default TextReplaceBuilder;
